import {
  Button,
  Field,
  Form,
  Modal,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import { actions as appActions } from '@/api/app';

import { zodResolver } from '@hookform/resolvers/zod';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

const RESET_FORM_SCHEMA = z.object({
  emailAddress: z.email({
    error: 'Enter a valid email including "@" and domain',
  }),
});

const ForgotPasswordForm: FunctionComponent = (): JSX.Element => {
  const dispatch = useDispatch();
  const { pushToast } = useToastContext();

  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const passwordResetFeedbackModal = useMemo(() => {
    return (
      <Modal
        isOpen={showFeedbackModal}
        title="Reset password"
        onClose={() => setShowFeedbackModal(false)}
      >
        <Modal.Body>
          <p style={{ whiteSpace: 'pre' }}>
            {
              'Please check your emails for instructions to reset\n your password.'
            }
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button onClick={() => setShowFeedbackModal(false)}>Ok</Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    );
  }, [showFeedbackModal]);

  const formMethods = useForm({
    resolver: zodResolver(RESET_FORM_SCHEMA),
    defaultValues: {
      emailAddress: '',
    },
  });

  const onSubmit: SubmitHandler<{ emailAddress: string }> = useCallback(
    (data) => {
      setIsResettingPassword(true);

      dispatch(
        appActions.sendPasswordResetEmail(data.emailAddress, (error: any) => {
          setIsResettingPassword(false);

          if (error) {
            pushToast({
              variant: 'solid',
              colour: 'red',
              message: 'Something went wrong. Please try again later.',
            });
          } else {
            setShowFeedbackModal(true);
          }
        })
      );
    },
    [dispatch, pushToast]
  );

  return (
    <div>
      <Form
        methods={formMethods}
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <Controller
          name="emailAddress"
          render={({ field: { value, onChange, onBlur, name } }) => (
            <Field name={name}>
              <Field.Label>Email</Field.Label>

              <RawInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                type="email"
                placeholder="Email address"
              />
            </Field>
          )}
        />

        <div className="button-wrapper">
          <Button type="submit" isProcessing={isResettingPassword}>
            Email me a reset link
          </Button>
        </div>
      </Form>
      {passwordResetFeedbackModal}
    </div>
  );
};

export default ForgotPasswordForm;
